import { LockKeyhole, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { extractErrorMessage, loginRequest, requestPasswordReset } from "../../lib/api";
import { buildAppUrl } from "../../lib/url";
import { LumenBrand } from "../common/LumenBrand";

const TRUCK_SWITCH_INTERVAL_MS = 4200;
const RESET_PASSWORD_URL = buildAppUrl("/reset-password");

const TruckWheel = ({ cx, cy, radius = 29, hubRadius = 12 }) => (
  <g className="login-wheel-group" transform={`translate(${cx} ${cy})`}>
    <circle cx="0" cy="0" fill="rgba(12,10,9,0.94)" r={radius} />
    <circle cx="0" cy="0" fill="rgba(228,228,231,0.88)" r={hubRadius} />
    <g className="login-wheel-hub-spin">
      <path d={`M 0 ${-hubRadius + 2} L 0 ${hubRadius - 2}`} stroke="rgba(39,39,42,0.45)" strokeLinecap="round" strokeWidth="2.5" />
      <path d={`M ${-hubRadius + 2} 0 L ${hubRadius - 2} 0`} stroke="rgba(39,39,42,0.45)" strokeLinecap="round" strokeWidth="2.5" />
      <path d={`M ${-(hubRadius - 3) * 0.7} ${-(hubRadius - 3) * 0.7} L ${(hubRadius - 3) * 0.7} ${(hubRadius - 3) * 0.7}`} stroke="rgba(39,39,42,0.35)" strokeLinecap="round" strokeWidth="2" />
      <path d={`M ${-(hubRadius - 3) * 0.7} ${(hubRadius - 3) * 0.7} L ${(hubRadius - 3) * 0.7} ${-(hubRadius - 3) * 0.7}`} stroke="rgba(39,39,42,0.35)" strokeLinecap="round" strokeWidth="2" />
    </g>
  </g>
);

const SharedTruckCab = () => (
  <g>
    <path
      d="M361 138H366C382 138 395 125 395 109V63H446C456 63 466 66 474 71L530 111V156H466C459 177 440 192 417 192C395 192 376 177 369 156H361V138Z"
      fill="url(#truck-body-gradient)"
    />
    <path d="M409 81H443C450 81 457 84 462 89L490 115H409V81Z" fill="rgba(244,244,245,0.72)" />
    <path d="M451 116H504" stroke="rgba(24,24,27,0.28)" strokeLinecap="round" strokeWidth="5" />
    <path d="M440 140H508" stroke="rgba(250,250,250,0.1)" strokeLinecap="round" strokeWidth="8" />
    <TruckWheel cx={417} cy={156} />
    <path d="M392 157H528" stroke="rgba(245,158,11,0.5)" strokeLinecap="round" strokeWidth="6" />
  </g>
);

const TankerTruckFrame = ({ active }) => (
  <g className={`login-truck-frame ${active ? "login-truck-frame-active" : "login-truck-frame-inactive"}`}>
    <path
      d="M96 138H332C348 138 361 125 361 109V90C361 74 348 61 332 61H164C150 61 137 73 136 88L134 100H96C74 100 56 118 56 140V156H82C88 177 107 192 130 192C153 192 171 177 178 156H369C376 177 395 192 417 192C440 192 459 177 466 156H530V111L474 71C466 66 456 63 446 63H395V109C395 125 382 138 366 138Z"
      fill="url(#truck-body-gradient)"
    />
    <TruckWheel cx={130} cy={156} />
    <path d="M160 83H316" stroke="rgba(250,250,250,0.12)" strokeLinecap="round" strokeWidth="8" />
    <path d="M76 157H404" stroke="rgba(245,158,11,0.5)" strokeLinecap="round" strokeWidth="6" />
    <SharedTruckCab />
  </g>
);

const DryvanTruckFrame = ({ active }) => (
  <g className={`login-truck-frame ${active ? "login-truck-frame-active" : "login-truck-frame-inactive"}`}>
    <rect fill="url(#truck-body-gradient)" height="76" rx="12" width="278" x="76" y="76" />
    <rect fill="rgba(250,250,250,0.08)" height="8" rx="4" width="236" x="94" y="94" />
    <rect fill="rgba(24,24,27,0.84)" height="18" rx="6" width="264" x="84" y="149" />
    <path d="M84 157H402" stroke="rgba(245,158,11,0.5)" strokeLinecap="round" strokeWidth="6" />
    <TruckWheel cx={140} cy={156} radius={28} hubRadius={11} />
    <TruckWheel cx={274} cy={156} radius={28} hubRadius={11} />
    <SharedTruckCab />
  </g>
);

const FlatbedTruckFrame = ({ active }) => (
  <g className={`login-truck-frame ${active ? "login-truck-frame-active" : "login-truck-frame-inactive"}`}>
    <path
      d="M76 146H346C356 146 364 138 364 128V122H84C70 122 58 133 58 146V155H75C81 173 96 186 114 186C132 186 147 173 153 155H270C276 173 291 186 309 186C327 186 342 173 348 155H402V146H76Z"
      fill="rgba(24,24,27,0.92)"
    />
    <rect fill="url(#truck-body-gradient)" height="42" rx="10" width="124" x="118" y="92" />
    <rect fill="url(#truck-body-gradient)" height="34" rx="10" width="82" x="252" y="100" />
    <path d="M78 156H404" stroke="rgba(245,158,11,0.5)" strokeLinecap="round" strokeWidth="6" />
    <TruckWheel cx={114} cy={155} radius={27} hubRadius={10} />
    <TruckWheel cx={309} cy={155} radius={27} hubRadius={10} />
    <SharedTruckCab />
  </g>
);

const LoginBackdrop = () => {
  const [activeTruckIndex, setActiveTruckIndex] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveTruckIndex((current) => (current + 1) % 3);
    }, TRUCK_SWITCH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="login-background-scene">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(251,191,36,0.5),transparent_20%),radial-gradient(circle_at_78%_22%,rgba(56,189,248,0.25),transparent_18%),radial-gradient(circle_at_58%_82%,rgba(249,168,212,0.18),transparent_16%),linear-gradient(135deg,#f8f4eb_0%,#fbfcff_48%,#eaf0f8_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.44)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.44)_1px,transparent_1px)] bg-[size:72px_72px] opacity-60" />

      <div className="login-orb-float absolute left-[8%] top-[9%] h-56 w-56 rounded-full bg-amber-300/40 blur-3xl" />
      <div className="login-orb-float-delayed absolute right-[10%] top-[14%] h-64 w-64 rounded-full bg-sky-300/30 blur-3xl" />
      <div className="login-orb-float absolute bottom-[10%] left-[18%] h-52 w-52 rounded-full bg-rose-200/40 blur-3xl" />

      <svg
        aria-hidden="true"
        className="absolute inset-0 h-full w-full opacity-80"
        data-testid="login-route-lines"
        preserveAspectRatio="none"
        viewBox="0 0 1600 1000"
      >
        <path className="login-route-path" d="M42 212C180 94 302 150 430 242C558 334 650 335 770 265C889 194 1034 107 1180 178C1327 249 1408 360 1555 314" />
        <path className="login-route-path-alt" d="M88 730C230 640 306 575 450 618C594 661 673 777 854 745C1034 713 1181 559 1334 600C1488 641 1520 729 1564 776" />
        <path className="login-route-path" d="M216 920C336 806 420 748 566 770C712 792 811 902 1006 853C1200 804 1318 650 1510 594" />
        <circle cx="430" cy="242" r="9" fill="rgba(255,255,255,0.8)" />
        <circle cx="770" cy="265" r="9" fill="rgba(255,255,255,0.82)" />
        <circle cx="1180" cy="178" r="9" fill="rgba(255,255,255,0.8)" />
        <circle cx="854" cy="745" r="9" fill="rgba(255,255,255,0.82)" />
        <circle cx="1334" cy="600" r="9" fill="rgba(255,255,255,0.8)" />
      </svg>

      <div className="absolute bottom-14 right-8 hidden w-[28rem] lg:block" data-testid="login-truck-silhouette">
        <svg aria-hidden="true" className="w-full" viewBox="0 0 620 220">
          <defs>
            <linearGradient id="truck-body-gradient" x1="0%" x2="100%" y1="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(24,24,27,0.94)" />
              <stop offset="100%" stopColor="rgba(63,63,70,0.84)" />
            </linearGradient>
          </defs>
          <TankerTruckFrame active={activeTruckIndex === 0} />
          <DryvanTruckFrame active={activeTruckIndex === 1} />
          <FlatbedTruckFrame active={activeTruckIndex === 2} />
        </svg>
      </div>
    </div>
  );
};

export const LoginPage = ({ onAuthenticated }) => {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Lumen TMS · Sign in";
  }, []);

  const handleChange = (event) => {
    setCredentials((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const payload = await loginRequest(credentials);
      onAuthenticated(payload);
      toast.success("Welcome back.");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to sign in with those credentials."));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordResetRequest = async (event) => {
    event.preventDefault();
    setResetSubmitting(true);
    try {
      await requestPasswordReset({
        email: resetEmail,
        redirect_to: RESET_PASSWORD_URL,
      });
      toast.success("If that email exists, a reset link is on the way.");
      setResetDialogOpen(false);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to send the password reset email."));
    } finally {
      setResetSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f8f4eb]" data-testid="login-page">
      <LoginBackdrop />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-8 sm:px-6 lg:px-10">
        <div className="login-card-rise w-full max-w-[28rem]">
          <div className="mb-5 text-center" data-testid="login-external-brand">
            <div className="mx-auto inline-flex rounded-[1.5rem] border border-white/55 bg-[linear-gradient(135deg,rgba(255,255,255,0.10),rgba(255,248,242,0.04))] px-5 py-3 shadow-[0_12px_24px_rgba(15,23,42,0.045)] backdrop-blur-xl backdrop-saturate-150" data-testid="login-brand-badge">
              <LumenBrand testId="login-brand" />
            </div>
          </div>

          <div className="panel-surface relative overflow-hidden rounded-[2rem] border border-white/70 bg-white/88 px-5 py-6 shadow-[0_30px_90px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:px-7 sm:py-8" data-testid="login-card">
            <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/80 to-transparent" />

            <div className="space-y-6">
              <div className="space-y-4 text-center" data-testid="login-header-block">
                <div>
                  <h1 className="mt-4 text-4xl font-semibold text-zinc-900 sm:text-5xl" data-testid="login-page-title">
                    Sign in
                  </h1>
                </div>
              </div>

              <form className="space-y-5" onSubmit={handleSubmit} data-testid="login-form">
              <label className="block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Email</span>
                <input
                  className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10"
                  data-testid="login-email-input"
                  name="email"
                  onChange={handleChange}
                  type="email"
                  value={credentials.email}
                />
              </label>

              <label className="block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Password</span>
                <div className="relative">
                  <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <input
                    className="w-full rounded-2xl border border-zinc-200 bg-white py-3 pl-11 pr-4 text-sm text-zinc-900 outline-none transition focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10"
                    data-testid="login-password-input"
                    name="password"
                    onChange={handleChange}
                    type="password"
                    value={credentials.password}
                  />
                </div>
              </label>

              <div className="flex justify-end">
                <button
                  className="text-sm font-medium text-zinc-500 transition hover:text-zinc-900"
                  data-testid="login-forgot-password-button"
                  onClick={() => {
                    setResetEmail(credentials.email);
                    setResetDialogOpen(true);
                  }}
                  type="button"
                >
                  Forgot password?
                </button>
              </div>

              <button
                className="inline-flex w-full items-center justify-center rounded-2xl bg-zinc-900 px-4 py-3 text-sm font-semibold text-white transition duration-200 hover:-translate-y-0.5 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-70"
                data-testid="login-form-submit-button"
                disabled={submitting}
                type="submit"
              >
                {submitting ? "Signing in..." : "Sign in to dashboard"}
              </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Dialog onOpenChange={setResetDialogOpen} open={resetDialogOpen}>
        <DialogContent className="max-w-md rounded-[1.75rem] p-6" data-testid="login-password-reset-dialog">
          <DialogHeader>
            <DialogTitle data-testid="login-password-reset-title">Reset your password</DialogTitle>
            <DialogDescription data-testid="login-password-reset-description">Enter your office email and we’ll send a secure reset link. If email sending is temporarily limited, ask an admin to generate a manual reset link from Users.</DialogDescription>
          </DialogHeader>

          <form className="space-y-4" data-testid="login-password-reset-form" onSubmit={handlePasswordResetRequest}>
            <label className="block space-y-2">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Email</span>
              <input
                className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10"
                data-testid="login-password-reset-email-input"
                onChange={(event) => setResetEmail(event.target.value)}
                required
                type="email"
                value={resetEmail}
              />
            </label>

            <div className="flex justify-end gap-3">
              <button className="rounded-2xl border border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-600" data-testid="login-password-reset-cancel-button" onClick={() => setResetDialogOpen(false)} type="button">Cancel</button>
              <button className="rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70" data-testid="login-password-reset-submit-button" disabled={resetSubmitting} type="submit">{resetSubmitting ? "Sending..." : "Send reset link"}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};